import React, { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

const SITE = {
  name: "Premier Pumps",
  url: "https://www.premierpumps.com",
  defaultTitle: "Premier Pumps | Industrial Vacuum Pumps & Systems",
  defaultDescription:
    "Premier Pumps provides industrial liquid ring vacuum pumps and systems for global industries, with installations in more than 40 countries.",
  logo: "https://www.premierpumps.com/logo-hd.png",
  locale: "en_US",
  sameAs: ["https://www.linkedin.com/company/premier-pumps/"],
};

const ROUTE_META = [
  {
    path: "/",
    title: SITE.defaultTitle,
    description: SITE.defaultDescription,
  },
  {
    path: "/products",
    title: "Products | Premier Pumps",
    description:
      "Explore Premier Pumps product lines including liquid ring vacuum pumps, systems, and accessories.",
  },
  {
    path: "/industries",
    title: "Industries | Premier Pumps",
    description:
      "Industry solutions for paper, power, chemical, oil & gas, mining, water treatment, and more.",
  },
  {
    path: "/download",
    title: "Downloads | Premier Pumps",
    description: "Access brochures, specifications, and product downloads.",
  },
  {
    path: "/contact-us",
    title: "Contact Us | Premier Pumps",
    description:
      "Contact Premier Pumps for sales inquiries, support, and service requests.",
  },
  {
    path: "/about-us",
    title: "About Us | Premier Pumps",
    description:
      "Learn about Premier Pumps, manufacturing capabilities, and global reach.",
  },
  {
    path: "/careers",
    title: "Careers | Premier Pumps",
    description: "Explore career opportunities at Premier Pumps.",
  },
  {
    path: "/sales-partners",
    title: "Sales Partners | Premier Pumps",
    description: "Find Premier Pumps sales partners and dealer network.",
  },
  {
    path: "/faqs",
    title: "FAQs | Premier Pumps",
    description: "Frequently asked questions about Premier Pumps products and services.",
  },
  {
    path: "/terms",
    title: "Terms of Service | Premier Pumps",
    description: "Read the Premier Pumps terms of service.",
  },
  {
    path: "/privacy",
    title: "Privacy Policy | Premier Pumps",
    description: "Read the Premier Pumps privacy policy.",
  },
  {
    path: "/news",
    title: "News | Premier Pumps",
    description: "Latest updates, writing, and announcements from Premier Pumps.",
  },
];

const DYNAMIC_META = [
  {
    path: "/product-details/:category",
    title: "Product Details | Premier Pumps",
    description:
      "Detailed specifications, features, and applications for Premier Pumps products.",
  },
  {
    path: "/writing-details/:id",
    title: "News | Premier Pumps",
    description: "Read the latest updates and writing from Premier Pumps.",
  },
];

const NO_INDEX_PATHS = ["/thank-you"];

function resolveMeta(pathname) {
  const staticMatch = ROUTE_META.find((route) =>
    matchPath({ path: route.path, end: true }, pathname)
  );
  if (staticMatch) return staticMatch;

  const dynamicMatch = DYNAMIC_META.find((route) =>
    matchPath({ path: route.path, end: true }, pathname)
  );
  return (
    dynamicMatch || {
      title: SITE.defaultTitle,
      description: SITE.defaultDescription,
    }
  );
}

function normalizePath(pathname) {
  if (pathname === "/") return "/";
  return pathname.replace(/\/+$/, "");
}

function Seo() {
  const { pathname } = useLocation();
  const meta = resolveMeta(pathname);
  const canonical = `${SITE.url}${normalizePath(pathname)}`;
  const noIndex = NO_INDEX_PATHS.some((path) =>
    matchPath({ path, end: true }, pathname)
  );

  useEffect(() => {
    const setMeta = (key, value, attr = "name") => {
      if (!value) return;
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", value);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
    };

    document.title = meta.title;
    setMeta("description", meta.description);
    setLink("canonical", canonical);

    setMeta("og:site_name", SITE.name, "property");
    setMeta("og:title", meta.title, "property");
    setMeta("og:description", meta.description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", SITE.logo, "property");
    setMeta("og:locale", SITE.locale, "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", meta.title);
    setMeta("twitter:description", meta.description);
    setMeta("twitter:image", SITE.logo);

    const robotsTag = document.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (robotsTag) {
        robotsTag.setAttribute("content", "noindex, nofollow");
      } else {
        const tag = document.createElement("meta");
        tag.setAttribute("name", "robots");
        tag.setAttribute("content", "noindex, nofollow");
        document.head.appendChild(tag);
      }
    } else if (robotsTag && robotsTag.getAttribute("content") === "noindex, nofollow") {
      robotsTag.remove();
    }

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE.name,
      url: SITE.url,
      logo: SITE.logo,
      sameAs: SITE.sameAs,
    };

    let schemaTag = document.getElementById("org-schema");
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.setAttribute("type", "application/ld+json");
      schemaTag.setAttribute("id", "org-schema");
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify(organizationSchema);
  }, [canonical, meta.description, meta.title, noIndex, pathname]);

  return null;
}

export default Seo;
